"use client";

import * as React from "react";
import { Calendar, ChevronDown } from "lucide-react";

import { CORE_COLORS, CORE_FONT } from "@/lib/theme/core";
import { DatePicker, fiveYearsAgo, today } from "./date-picker";

function pad(n: number) {
  return String(n).padStart(2, "0");
}
function iso(d: Date) {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/**
 * The 出版日 publication-date control.
 *
 * Was a bare `<button>` with no handler and no calendar, duplicated byte-for-byte
 * in `components/shared/main/category-header.tsx` and
 * `components/live-news/live-news-header.tsx`. QA: "1. 懸停時，將游標圖示改為手指
 * 圖示。 2. 應該顯示一個日期選擇器元件來進行篩選".
 *
 * Opens a real picker and reflects the chosen date. It does NOT filter: the
 * listing fixtures behind these headers carry a relative `time` ("23:56",
 * "15分鐘前") and no publication date, so there is no field to compare against.
 * Deliberately left as presentation until the data provides a date — faking it
 * would mean inventing a mapping. Lift `value` into the page and filter there
 * once that lands.
 */
export function PublicationDateButton({
  value,
  onChange,
}: {
  /** Controlled date. Omit to keep the state internal. */
  value?: Date;
  onChange?: (next: Date) => void;
}) {
  // Defaults to today — the publication a reader lands on. QA also set the
  // selectable window: no earlier than five years ago, no later than today.
  const [own, setOwn] = React.useState<Date>(() => today());
  const date = value ?? own;
  const setDate = (next: Date) => {
    if (value === undefined) setOwn(next);
    onChange?.(next);
  };

  return (
    <DatePicker
      label="選擇出版日期"
      value={date}
      onChange={setDate}
      min={fiveYearsAgo()}
      max={today()}
    >
      <button
        type="button"
        aria-label={`選擇出版日期,目前 ${iso(date)}`}
        // QA: no background change on hover — the date label recolours instead.
        className="inline-flex items-center gap-[10px] group rounded-[3px] bg-[color:var(--rest)] px-[6px] py-[4px] transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
        style={
          {
            ["--rest" as string]: "#ffffff",
            "--tw-ring-color": CORE_COLORS.brandGreen,
          } as React.CSSProperties
        }
      >
        <span
          className="inline-flex items-center justify-center shrink-0"
          style={{
            width: 32,
            height: 32,
            borderRadius: 3,
            backgroundColor: "#f5f7f6",
            color: CORE_COLORS.brandGreen,
          }}
        >
          <Calendar size={14} strokeWidth={1.75} />
        </span>
        <span className="flex flex-col items-start leading-none">
          <span
            style={{
              color: CORE_COLORS.textTertiary,
              fontFamily: CORE_FONT.sans,
              fontSize: 11,
              fontWeight: 500,
              letterSpacing: "0.4px",
              textTransform: "uppercase",
              marginBottom: 2,
            }}
          >
            出版日
          </span>
          {/* Resting colour is a custom property, not an inline `color`: an
              inline declaration outranks the `group-hover:` class. */}
          <span
            className="text-[color:var(--rest-fg)] transition-colors duration-150 group-hover:text-[color:var(--hover)]"
            style={{
              ["--hover" as string]: CORE_COLORS.subscribeGreen,
              ["--rest-fg" as string]: CORE_COLORS.textPrimary,
              fontFamily: CORE_FONT.sans,
              fontSize: 15,
              fontWeight: 600,
              letterSpacing: "-0.224px",
              lineHeight: "18px",
            }}
          >
            {iso(date)}
          </span>
        </span>
        <ChevronDown
          size={14}
          strokeWidth={2}
          color={CORE_COLORS.textTertiary}
          className="shrink-0 transition-transform duration-150 group-hover:translate-y-[1px]"
          style={{ marginLeft: 2 }}
        />
      </button>
    </DatePicker>
  );
}
